const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:4000').replace(/\/$/, '');

export function mediaUrl(url) {
  if (!url) return '';
  const value = String(url).trim();
  if (!value) return '';
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
    return value;
  }
  if (value.startsWith('/')) return `${API_BASE}${value}`;
  if (value.startsWith('uploads/')) return `${API_BASE}/${value}`;
  return value;
}

async function apiFetch(path, { method = 'GET', headers = {}, body, token, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    signal,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }

  return data;
}


async function apiUpload(path, { files = [], fields = {}, token } = {}) {
  const form = new FormData();
  Array.from(files || []).forEach((file) => form.append('files', file));
  Object.entries(fields || {}).forEach(([k, v]) => form.append(k, v == null ? '' : String(v)));
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: form,
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!res.ok) {
    const msg = data?.error || data?.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

export const api = {
  // public
  getCategories: () => apiFetch('/api/categories'),
  getBrands: () => apiFetch('/api/brands'),
  getBrandGroups: () => apiFetch('/api/brands/groups/level1'),
  getBrandBySlug: (slug) => apiFetch(`/api/brands/slug/${encodeURIComponent(slug)}`),
  getProductsByCategorySlug: (slug, params = {}) => {
    const qs = new URLSearchParams({ ...params, category: slug }).toString();
    return apiFetch(`/api/products${qs ? `?${qs}` : ''}`);
  },
  getProducts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/api/products${qs ? `?${qs}` : ''}`);
  },
  getProductById: (id) => apiFetch(`/api/products/${id}`),
  getCategoryBySlug: (slug) => apiFetch(`/api/categories/slug/${encodeURIComponent(slug)}`),
  getCategoryBrands: (slug) => apiFetch(`/api/categories/slug/${encodeURIComponent(slug)}/brands`),
  getCatalogProducts: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/api/catalog/products${qs ? `?${qs}` : ''}`);
  },
  getCatalogRows: (paramsOrId = {}) => {
  // Backward compatible:
  // - If called with an object => /api/catalog/rows?...
  // - If called with a product id => fetch product and return a single-row table-like payload
  if (paramsOrId !== null && typeof paramsOrId !== 'object') {
    const id = paramsOrId;
    return apiFetch(`/api/products/${encodeURIComponent(id)}`).then((res) => {
      const p = res?.product || res?.data || res;
      const row = {
        id: p?.id,
        price: p?.price,
        stock_qty: p?.stock_qty,
        unit: p?.unit,
        ...(p?.tech_data && typeof p.tech_data === 'object' ? p.tech_data : {}),
      };
      return { data: row?.id ? [row] : [] };
    });
  }
  const qs = new URLSearchParams(paramsOrId).toString();
  return apiFetch(`/api/catalog/rows${qs ? `?${qs}` : ''}`);
},
  searchProducts: (q) => apiFetch(`/api/search?q=${encodeURIComponent(q || '')}`),
  getProductCatalogs: () => apiFetch('/api/product-catalogs'),
  getProductCatalog: (id) => apiFetch(`/api/product-catalogs/${encodeURIComponent(id)}`),
  getProductCatalogFileUrl: (id) => `${API_BASE}/api/product-catalogs/${encodeURIComponent(id)}/file`,
  getProductCatalogDownloadUrl: (id) => `${API_BASE}/api/product-catalogs/${encodeURIComponent(id)}/download`,
  getEngineeringLibraryRoot: () => apiFetch('/api/engineering-library/root'),
  getEngineeringLibraryNode: (id) => apiFetch(`/api/engineering-library/nodes/${encodeURIComponent(id)}`),

  // preinvoice
  getCart: (token, signal) => apiFetch('/api/web/cart', { token, signal }),
  applyCartOperation: (token, body, signal) => apiFetch('/api/web/cart/operations', { method: 'POST', token, body, signal }),
  createPreinvoice: (token, payload) => apiFetch('/api/preinvoices', { method: 'POST', token, body: payload }),
  uploadPreinvoiceFiles: (token, files, fields = {}) => apiUpload('/api/preinvoices/upload', { token, files, fields }),

  // contact
  createContactMessage: (payload) => apiFetch('/api/contact-messages', { method: 'POST', body: payload }),

  // web auth
  requestOtp: (phone) => apiFetch('/api/web-auth/request-otp', { method: 'POST', body: { phone } }),
  verifyOtp: (payload) => apiFetch('/api/web-auth/verify-otp', { method: 'POST', body: payload }),
  loginPassword: (phone, password) => apiFetch('/api/web-auth/login-password', { method: 'POST', body: { phone, password } }),
  webMe: (token) => apiFetch('/api/web-auth/me', { token }),
  webUpdateProfile: (token, payload) => apiFetch('/api/web/profile', { method: 'PUT', token, body: payload }),
  webPreinvoices: (token, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiFetch(`/api/web/preinvoices${qs ? `?${qs}` : ''}`, { token });
  },
  webPreinvoiceById: (token, id) => apiFetch(`/api/web/preinvoices/${id}`, { token }),
  webPreinvoiceDetail: (token, id) => apiFetch(`/api/web/preinvoices/${id}`, { token }),

  cancelPreinvoice: (token, id) =>
    apiFetch(`/api/web/preinvoices/${id}/cancel`, {
      method: 'POST',
      token
    }),

  downloadPreinvoicePdf: (token, id) =>
    `${API_BASE}/api/web/preinvoices/${id}/pdf?token=${token}`,

};

export { API_BASE };
